import React, { ReactNode, useMemo } from "react";
import { css, Theme, useTheme } from "@emotion/react";

import RoundButton from "core/Components/Buttons/RoundButton";
import ConfirmationCloseButtonIcon from "pages/Components/CartAndCheckout/components/ConfirmationCloseButtonIcon";
import { isDown } from "theme/breakpoints";
import useBreakpoint from "core/hooks/useBreakpoint";

type Props = {
    onCancel: () => void,
    onConfirm: () => void,
    cancelLabel?: string,
    confirmLabel?: string,
    title?: string,
    message?: string | ReactNode,
    className?: string,
}

const ConfirmationModal = (
    {
        onCancel,
        onConfirm,
        cancelLabel = "Cancel",
        confirmLabel = "Confirm",
        title = "",
        message = "",
        className = "",
    }: Props
) => {
    const theme: Theme = useTheme();
    const { breakpoint } = useBreakpoint();

    const [buttonHeight, buttonTextFontSize] = useMemo(() => {
        switch (true) {
            case isDown(breakpoint, 'xs'):
                return [38, 14];
            default:
                return [50, 19];
        }
    }, [breakpoint]);

    const containerStyle = css`
        position: relative;
        display: flex;
        flex-direction: column;
        justify-content: space-between;
        align-items: center;
        padding: 0 35px 20px;
        width: 490px;
        height: 290px;
        border-radius: 17px;
        background-color: white;

        ${theme.breakpoints.only('sm')} {
            width: 400px;
        }

        ${theme.breakpoints.down('xs')} {
            width: 85%;
        }
    `;

    const confirmationContentStyle = css`
        margin-top: 30px;
        font-size: 18px;
        line-height: 23px;
        text-align: center;

        ${theme.breakpoints.down('sm')} {
            margin-top: 32px;
            font-size: 16px;
        }
    `;

    const confirmationButtonContainerStyle = css`
        display: flex;
        justify-content: space-between;
        width: 100%;
        gap: 20px;
    `;

    const style = css`
        position: fixed;
        display: flex;
        justify-content: center;
        align-items: center;

        top: 0;
        width: 100%;
        height: 100%;
        background-color: rgba(0, 0, 0, 0.3);
        z-index: ${theme.zIndex.zIndexHigh};
    `;

    const titleStyle = css`
        font-size: 28px;
        font-weight: ${theme.fonts.weights.bold};
        margin-bottom: 25px;

        ${theme.breakpoints.down('xs')} {
            font-size: 24px;
        }
    `;

    const buttonStyle = css`
        flex: 1
    `;

    return (
        <div css={style} className={className}>
            <div css={containerStyle} className={'confirmation-modal-container'}>
                <div css={confirmationContentStyle} className={'confirmation-modal-content'}>
                    {title && (
                        <div css={titleStyle}>{title}</div>
                    )}
                    <div className={'confirmation-modal-message'}>{message}</div>
                </div>
                <div css={confirmationButtonContainerStyle} className={'confirmation-modal-buttons'}>
                    <RoundButton
                        onButtonClick={onCancel}
                        buttonHeight={buttonHeight}
                        textFontSize={buttonTextFontSize}
                        backgroundColour={theme.colours.boulder2}
                        buttonHoverColour={theme.colours.silverChalice4}
                        textColour={theme.colours.white}
                        useTextHover={false}
                        css={buttonStyle}
                    >
                        {cancelLabel}
                    </RoundButton>
                    <RoundButton
                        onButtonClick={onConfirm}
                        buttonHeight={buttonHeight}
                        textFontSize={buttonTextFontSize}
                        backgroundColour={theme.colours.curiousBlue2}
                        buttonHoverColour={theme.colours.malibu3}
                        useTextHover={false}
                        textColour={theme.colours.white}
                        css={buttonStyle}
                    >
                        {confirmLabel}
                    </RoundButton>
                </div>
                <ConfirmationCloseButtonIcon onClose={onCancel} />
            </div>
        </div>
    );
};

export default ConfirmationModal;